import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Cancel,
  CheckCircle,
  DeleteForever,
  Edit,
  Info,
} from "@mui/icons-material";
import {
  BookmarkDto,
  BookmarksDto,
  BookmarkTypes,
} from "../../../../../../customer-experience/src/lib/ShiOneClient";

export default function PowerBiPersonalViews({
  contactBookmarks,
  handleApplyBookmark,
  addBookmark,
  deleteBookmark,
  updateBookmark,
}: Readonly<{
  contactBookmarks: BookmarkDto[] | undefined;
  handleApplyBookmark: (bookmark: BookmarksDto) => void;
  addBookmark: (name: string, type: BookmarkTypes) => void;
  deleteBookmark: (bookmark: BookmarksDto) => void;
  updateBookmark: (bookmark: BookmarksDto) => void;
}>) {
  const theme = useTheme();

  const [changedBookmark, setChangedBookmark] = useState<BookmarkDto>(
    new BookmarkDto(),
  );
  const [addingContactBookmark, setAddingContactBookmark] = useState(false);
  const [contactBookmarkName, setContactBookmarkName] =
    useState("New Bookmark");

  const handleNewBookmarkName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setContactBookmarkName(value);
  };

  const handleUpdateBookmarkName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setChangedBookmark((prevState) => {
      const updated = new BookmarkDto(prevState);
      updated.name = value;
      return updated;
    });
  };

  const handleCancelNewBookmark = () => {
    setAddingContactBookmark(false);
    setContactBookmarkName("New Bookmark");
  };

  const handleCreateBookmark = () => {
    addBookmark(contactBookmarkName, BookmarkTypes.User);
    setContactBookmarkName("New Bookmark");
    setAddingContactBookmark(false);
  };

  const handleSaveUpdatedBookmark = () => {
    updateBookmark(changedBookmark);
    setChangedBookmark(new BookmarkDto());
  };

  const handleDeleteBookmark = (bookmark: BookmarkDto) => {
    deleteBookmark(bookmark);
  };

  const handleBookmarkSelect = (bookmark: BookmarkDto) => {
    handleApplyBookmark(bookmark);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: theme.spacing(1.5),
          width: "100%",
          backgroundColor: theme.palette.background.dark,
        }}
      >
        <Box
          component="span"
          sx={{
            alignSelf: "center",
          }}
        >
          Personal Views&nbsp;
          <Tooltip title="Personal views are private to you and let you save and return to your custom view of the report">
            <Info color="info" fontSize={"small"} />
          </Tooltip>
        </Box>
        <Button
          size={"small"}
          color={"primary"}
          variant={"contained"}
          onClick={() => setAddingContactBookmark(true)}
        >
          Add
        </Button>
      </Box>
      <List disablePadding>
        {contactBookmarks?.map((bm) => {
          const currentlyUpdating =
            changedBookmark.bookmarkId === bm.bookmarkId &&
            changedBookmark.bookmarkId;

          return (
            <Box key={bm.bookmarkId}>
              <ListItem>
                {!currentlyUpdating && (
                  <>
                    <ListItemButton onClick={() => handleBookmarkSelect(bm)}>
                      {bm.name}
                    </ListItemButton>
                    <ListItemSecondaryAction>
                      <Tooltip title={"Edit"}>
                        <IconButton
                          color="primary"
                          onClick={() => setChangedBookmark(bm)}
                          size="large"
                        >
                          <Edit fontSize={"small"} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={"Delete"}>
                        <IconButton
                          color="secondary"
                          onClick={() => handleDeleteBookmark(bm)}
                          size="large"
                        >
                          <DeleteForever fontSize={"small"} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </>
                )}
                {currentlyUpdating && (
                  <>
                    <TextField
                      style={{
                        width: "75%",
                        padding: theme.spacing(1),
                      }}
                      onChange={handleUpdateBookmarkName}
                      value={changedBookmark.name}
                      variant={"standard"}
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title={"Cancel"}>
                        <IconButton
                          color="secondary"
                          onClick={() => setChangedBookmark(new BookmarkDto())}
                          size="large"
                        >
                          <Cancel fontSize={"small"} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={"Save"}>
                        <IconButton
                          color="success"
                          onClick={() => handleSaveUpdatedBookmark()}
                          size="large"
                        >
                          <CheckCircle fontSize={"small"} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </>
                )}
              </ListItem>
              <Divider />
            </Box>
          );
        })}
        {addingContactBookmark && (
          <>
            <ListItem>
              <TextField
                style={{
                  width: "75%",
                  padding: theme.spacing(1),
                }}
                name={BookmarkTypes.User}
                onChange={handleNewBookmarkName}
                value={contactBookmarkName}
                variant={"standard"}
              />
              <ListItemSecondaryAction>
                <Tooltip title={"Cancel"}>
                  <IconButton
                    color="secondary"
                    onClick={() => handleCancelNewBookmark()}
                    size="large"
                  >
                    <Cancel fontSize={"small"} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={"Save"}>
                  <IconButton
                    color="success"
                    onClick={() => handleCreateBookmark()}
                    size="large"
                  >
                    <CheckCircle fontSize={"small"} />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
          </>
        )}
      </List>
    </>
  );
}
